import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Edit } from 'lucide-react';
import { designationsAPI } from '../../lib/api.js';
import Card from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Badge from '../../components/ui/Badge.jsx';

function DesignationsPage() {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 10 });
  const [showFilters, setShowFilters] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', code: '', active: true });

  const { data, isLoading } = useQuery({
    queryKey: ['designations', search, activeFilter || 'all', pagination.page, pagination.limit],
    queryFn: () => designationsAPI.getAll({ search, active: activeFilter || undefined, page: pagination.page, limit: pagination.limit }),
    // Removed refetchInterval to prevent unnecessary API calls
    // refetchInterval: 30000,
  });

  // Fetch overall count (unfiltered)
  const { data: allCountData } = useQuery({
    queryKey: ['designations-total-count'],
    queryFn: () => designationsAPI.getAll({ page: 1, limit: 1 }),
    staleTime: 60000,
  });

  const rows = data?.data?.data || [];
  const pageInfo = data?.data?.pagination || { page: 1, limit: 10, total: 0, totalPages: 0, hasNext: false, hasPrev: false };
  const totalAll = allCountData?.data?.pagination?.total || 0;

  const resetForm = () => {
    setFormData({ name: '', code: '', active: true });
    setEditingItem(null);
  };

  const createMutation = useMutation({
    mutationFn: (payload) => designationsAPI.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries(['designations']);
      setShowModal(false);
      resetForm();
      alert('Designation created successfully');
    },
    onError: (error) => {
      const msg = error?.response?.data?.error?.message || error?.response?.data?.message || error?.message || 'Failed to create designation';
      alert(`Create failed: ${msg}`);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => designationsAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['designations']);
      setShowModal(false);
      resetForm();
      alert('Designation updated successfully');
    },
    onError: (error) => {
      const msg = error?.response?.data?.error?.message || error?.response?.data?.message || error?.message || 'Failed to update designation';
      alert(`Update failed: ${msg}`);
    }
  });

  const handleCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ name: item.name || '', code: item.code || '', active: !!item.active });
    setShowModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = { name: formData.name, code: formData.code, active: formData.active };
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header (match Users Management style) */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">Designations Management</h1>
        </div>
        {/* Right side: count + actions with comfortable spacing */}
        <div className="flex items-center gap-6 ml-auto">
          <div className="text-right">
            <div className="text-2xl font-bold text-teal-700 dark:text-teal-300 leading-tight">{totalAll}</div>
            <div className="text-xs text-gray-600 dark:text-gray-300">All Designations</div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              onClick={() => setShowFilters((s) => !s)}
              className="flex items-center gap-1 px-3 py-1.5 text-xs"
            >
              Filters
            </Button>
            <Button
              variant="primary"
              onClick={handleCreate}
              className="flex items-center gap-1 px-3 py-1.5 text-xs"
            >
              <Plus className="h-3 w-3" />
              Add Designation
            </Button>
          </div>
        </div>
      </div>

      {showFilters && (
        <Card>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input
              placeholder="Search by name or code..."
              value={search}
              onChange={(e) => { setPagination(p => ({ ...p, page: 1 })); setSearch(e.target.value); }}
              icon={<Search className="h-4 w-4" />}
            />
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">Active</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                value={activeFilter}
                onChange={(e) => { setPagination(p => ({ ...p, page: 1 })); setActiveFilter(e.target.value); }}
              >
                <option value="">All</option>
                <option value="true">true</option>
                <option value="false">false</option>
              </select>
            </div>
            <div className="flex items-center md:justify-end">
              <button
                type="button"
                className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                onClick={() => { setSearch(''); setActiveFilter(''); setPagination({ page: 1, limit: 10 }); }}
              >
                Clear filters
              </button>
            </div>
          </div>
        </Card>
      )}

      <Card className="w-full">
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="animate-pulse h-8 bg-gray-100 dark:bg-gray-700 rounded"></div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border border-teal-700">
              <thead>
                <tr className="bg-teal-700 border-b border-teal-600 dark:border-teal-600 divide-x divide-teal-600 dark:divide-teal-600">
                  <th className="text-left py-3 px-4 font-medium text-white dark:text-white">Name</th>
                  <th className="text-left py-3 px-4 font-medium text-white dark:text-white">Code</th>
                  <th className="text-left py-3 px-4 font-medium text-white dark:text-white">Active</th>
                  <th className="text-left py-3 px-4 font-medium text-white dark:text-white">Created</th>
                  <th className="text-left py-3 px-4 font-medium text-white dark:text-white">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((item) => (
                  <tr key={item.id} className="border-b border-teal-600 dark:border-teal-600 hover:bg-gray-50 dark:hover:bg-gray-800 divide-x divide-teal-600 dark:divide-teal-600">
                    <td className="py-3 px-4"><div className="font-medium text-gray-900 dark:text-white">{item.name}</div></td>
                    <td className="py-3 px-4 text-gray-900 dark:text-white">{item.code}</td>
                    <td className="py-3 px-4 text-gray-900 dark:text-white">{String(item.active)}</td>
                    <td className="py-3 px-4 text-gray-900 dark:text-white">{item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}</td>
                    <td className="py-3 px-4">
                      <Button variant="outline" size="sm" onClick={() => handleEdit(item)} className="px-3 py-1.5 text-xs">
                        <Edit className="h-4 w-4 mr-1" /> Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">No designations found</div>
            )}
          </div>
        )}
      </Card>

      <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
        <div>
          {pageInfo.total > 0 && (
            <span>
              Showing {Math.min((pageInfo.page - 1) * pageInfo.limit + 1, pageInfo.total)} to {Math.min(pageInfo.page * pageInfo.limit, pageInfo.total)} of {pageInfo.total} designations
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button className={`px-3 py-1 rounded border ${pageInfo.hasPrev ? 'hover:bg-gray-100 dark:hover:bg-gray-700' : 'opacity-50 cursor-not-allowed'}`} onClick={() => pageInfo.hasPrev && setPagination(p => ({ ...p, page: p.page - 1 }))} disabled={!pageInfo.hasPrev}>Previous</button>
          <span>Page {pageInfo.page} of {Math.max(pageInfo.totalPages, 1)}</span>
          <button className={`px-3 py-1 rounded border ${pageInfo.hasNext ? 'hover:bg-gray-100 dark:hover:bg-gray-700' : 'opacity-50 cursor-not-allowed'}`} onClick={() => pageInfo.hasNext && setPagination(p => ({ ...p, page: p.page + 1 }))} disabled={!pageInfo.hasNext}>Next</button>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{editingItem ? 'Edit Designation' : 'New Designation'}</h2>
              <Button variant="outline" size="sm" onClick={() => setShowModal(false)}>×</Button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                <Input name="name" value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} placeholder="Enter designation name" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Code</label>
                <Input name="code" value={formData.code} onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))} placeholder="Enter code" required />
              </div>
              <div className="flex items-center space-x-2">
                <input type="checkbox" name="active" checked={formData.active} onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Active</label>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button type="submit" variant="primary" disabled={createMutation.isPending || updateMutation.isPending}>{createMutation.isPending || updateMutation.isPending ? 'Saving...' : (editingItem ? 'Update' : 'Create')}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default DesignationsPage;


